import React from 'react';
import qs from 'qs';
import {withRouter, Link} from 'react-router';
import PropTypes from 'prop-types';
import {fromJS} from 'immutable';
import moment from 'moment';
import {stats} from 'lib/StatsUtil';
import {pluralize} from 'lib/stringUtils';
import {renderDuration} from 'lib/timeUtils';
import {resource} from '@albert-io/json-api-framework/request/builder';
import FlatBarChart from 'generic/FlatBarChart/FlatBarChart.react';
import {TooltipWrapper} from 'sg/Tooltip/Tooltip.react';
import {Button} from '@albert-io/atomic';
import {RANGES} from '@albert-io/atomic/organisms/DatePicker/DatePicker.helpers';

import classroomInsightsStore from '../../../ClassroomInsights.store';

class StandardsTable extends React.Component {
  static propTypes = {
    location: PropTypes.shape({
      query: PropTypes.shape({
        attempt: PropTypes.string,
        endDate: PropTypes.string,
        guideLevelId: PropTypes.string,
        startDate: PropTypes.string,
        subjectId: PropTypes.string,
        classId: PropTypes.string
      })
    }),
    params: PropTypes.shape({
      classId: PropTypes.string,
      studentId: PropTypes.string
    })
  };

  getStandardsQuery() {
    const {attempt, endDate, guideLevelId, startDate, subjectId} = this.props.location.query;
    const {classId, studentId} = this.props.params;

    const guessContext = {};
    if (attempt === 'mostRecent') {
      guessContext.most_recent = true;
    } else {
      guessContext.attempt_number = attempt;
    }

    return resource('labels_v1')
      .mandarkEndpoint(['labels_v1'])
      .filter({
        subject_v2: {
          id: subjectId
        },
        'meta.count_of_classroom_questions': {
          not: 0
        }
      })
      .filter(
        {
          questions_v1: {
            question_set_v1: {
              guide_levels_v2: guideLevelId
            }
          }
        },
        guideLevelId
      )
      .withMeta('label_v1')
      .meta({
        context: {
          classroom: {
            id: classId
          },
          end_date: endDate,
          guess: guessContext,
          label: {
            counts: true
          },
          start_date: startDate,
          student: {
            id: studentId
          },
          subject: {
            id: subjectId
          }
        }
      })
      .meta(
        {
          context: {
            guideLevel: {
              id: guideLevelId
            }
          }
        },
        guideLevelId
      );
  }

  renderAccuracy = (standard) => {
    const correctGuesses = this.renderCorrectAttempts(standard);
    const incorrectGuesses = this.renderIncorrectAttempts(standard);
    const totalGuesses = correctGuesses + incorrectGuesses;
    return `${stats.percentage(correctGuesses, totalGuesses)}%`;
  };

  renderAverageTimeSpent(standard) {
    const timeSpentDuration = moment.duration(
      standard.getMeta().getSumOfClassroomGuessesTimeElapsed()
    );
    return renderDuration(timeSpentDuration, {style: 'hh:mm:ss'});
  }

  renderCategory(standard) {
    const category = standard.getCategory();
    return category || 'N/A';
  }

  renderCorrectAttempts(standard) {
    return standard.getMeta().getCountOfStudentCorrectAttempts();
  }

  renderCountOfQuestions(standard) {
    return standard.getMeta().getCountOfClassroomQuestions();
  }

  renderIncorrectAttempts(standard) {
    return standard.getMeta().getCountOfStudentIncorrectAttempts();
  }

  renderName(standard) {
    return <TooltipWrapper tooltip={standard.getText()}>{standard.getName()}</TooltipWrapper>;
  }

  renderProgress = (standard) => {
    const correctGuesses = this.renderCorrectAttempts(standard);
    const incorrectGuesses = this.renderIncorrectAttempts(standard);
    const totalGuesses = this.renderTotalAttempts(standard);
    const unansweredGuesses = standard.getMeta().getCountOfClassroomQuestionsUnanswered();
    const totalPotentialGuesses = correctGuesses + incorrectGuesses + unansweredGuesses;

    const performanceData = fromJS([
      {
        fillColorClass: 'classroom-insights__performance--correct',
        fillPercentage: stats.percentage(correctGuesses, totalPotentialGuesses),
        keyLabel: 'Correct'
      },
      {
        fillColorClass: 'classroom-insights__performance--incorrect',
        fillPercentage: stats.percentage(incorrectGuesses, totalPotentialGuesses),
        keyLabel: 'Incorrect'
      },
      {
        fillColorClass: 'classroom-insights__performance--unanswered',
        fillPercentage: stats.percentage(unansweredGuesses, totalPotentialGuesses),
        keyLabel: 'Not Answered'
      }
    ]);

    return (
      <div className='usage-report__performance'>
        <small>
          {totalGuesses} {pluralize('total attempt', totalGuesses)}
        </small>
        <FlatBarChart inline data={performanceData} />
      </div>
    );
  };

  renderTotalAttempts = (standard) => {
    const correctGuesses = this.renderCorrectAttempts(standard);
    const incorrectGuesses = this.renderIncorrectAttempts(standard);
    return correctGuesses + incorrectGuesses;
  };

  renderStandardButton = (standard) => {
    const subject = classroomInsightsStore.getSubject();
    return (
      <Button
        whiteBg
        small
        onClick={(e) => e.stopPropagation()}
        as={Link}
        to={`/${subject.getUrlSlug()}?standards=${standard.getId()}`}
      >
        Find questions
      </Button>
    );
  };

  render() {
    const {classId, studentId} = this.props.params;
    const subjectId = classroomInsightsStore.getSubjectId();
    const attemptNumber = classroomInsightsStore.getAttemptNumber();
    const guideLevelId = classroomInsightsStore.getGuideLevelId();
    const [startDate] = new Date(classroomInsightsStore.getStartDate()).toISOString().split('T');
    const [endDate] = new Date(classroomInsightsStore.getEndDate()).toISOString().split('T');

    const reportsLink = `/reports/classrooms/${classId}/students/${studentId}/standards?${qs.stringify(
      {
        attempt: attemptNumber,
        end: endDate,
        guideLevel: guideLevelId === '' ? undefined : guideLevelId,
        range: RANGES.CUSTOM,
        start: startDate,
        subject: subjectId
      }
    )}`;

    return (
      <div className='u-display_flex u-justify-content_center u-pad_4'>
        <div>
          <h3>This report has been deprecated.</h3>
          <p>You can access the same data via the new reports feature.</p>
          <Button color='green' as='a' href={reportsLink}>
            Open Reports
          </Button>
        </div>
      </div>
    );
  }
}

export default withRouter(StandardsTable);
